import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { authenticateLecturer, LecturerCredentials } from '../services/lecturerAuth';
import { PasswordPromptModal } from '../components/common/PasswordPromptModal';
import toast from 'react-hot-toast';

interface LecturerAuthContextType {
  credentials: LecturerCredentials | null;
  isAuthenticated: boolean;
  allowAllTactics: boolean;
  toggleAllTactics: () => void;
  allowAllTheorems: boolean;
  toggleAllTheorems: () => void;
  requestLogin: () => void;
  logout: () => void;
}

const LecturerAuthContext = createContext<LecturerAuthContextType | undefined>(undefined);

export function LecturerAuthProvider({ children }: { children: ReactNode }) {
  // Keep credentials only in memory; saved game settings cannot grant lecturer access.
  const [credentials, setCredentials] = useState<LecturerCredentials | null>(null);
  const [allowAllTactics, setAllowAllTactics] = useState(false);
  const [allowAllTheorems, setAllowAllTheorems] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const requestLogin = useCallback(() => setShowLogin(true), []);
  const logout = useCallback(() => {
    setCredentials(null);
    setAllowAllTactics(false);
    setAllowAllTheorems(false);
  }, []);
  const toggleAllTactics = useCallback(() => {
    if (credentials) setAllowAllTactics(value => !value);
  }, [credentials]);
  const toggleAllTheorems = useCallback(() => {
    if (credentials) setAllowAllTheorems(value => !value);
  }, [credentials]);

  const login = async (username: string, password: string) => {
    const nextCredentials = { username, password };
    await authenticateLecturer(nextCredentials);
    setAllowAllTactics(false);
    setAllowAllTheorems(false);
    setCredentials(nextCredentials);
    setShowLogin(false);
    toast.success('Lecturer mode enabled');
  };

  return (
    <LecturerAuthContext.Provider
      value={{
        credentials,
        isAuthenticated: credentials !== null,
        allowAllTactics: credentials !== null && allowAllTactics,
        toggleAllTactics,
        allowAllTheorems: credentials !== null && allowAllTheorems,
        toggleAllTheorems,
        requestLogin,
        logout,
      }}
    >
      {children}
      <PasswordPromptModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onConfirm={login}
        title="Lecturer Login"
        message="Sign in to enable lecturer mode and view solutions."
      />
    </LecturerAuthContext.Provider>
  );
}

export function useLecturerAuth() {
  const context = useContext(LecturerAuthContext);
  if (context === undefined) {
    throw new Error('useLecturerAuth must be used within a LecturerAuthProvider');
  }
  return context;
}

